import {
  isConversationMessage,
  isInternalNoteType,
  isLogType,
} from "../components/MessageHistory/messageTypeRegistry";

export const EXPORT_FIELD_GROUPS = [
  { id: "solicitacao", label: "Solicitação" },
  { id: "historico", label: "Histórico" },
];

export const CONVERSATION_EXPORT_FIELDS = [
  { key: "id", label: "Protocolo", group: "solicitacao", defaultSelected: true },
  { key: "name", label: "Nome", group: "solicitacao", defaultSelected: true },
  { key: "lastMessage", label: "Última mensagem", group: "solicitacao", defaultSelected: true },
  { key: "createdAt", label: "Data de cadastro", group: "solicitacao", defaultSelected: true },
  { key: "lastUpdate", label: "Última atualização", group: "solicitacao", defaultSelected: true },
  { key: "typeName", label: "Tipo", group: "solicitacao", defaultSelected: true },
  { key: "subtypes", label: "Subtipos", group: "solicitacao", defaultSelected: true },
  { key: "status", label: "Status", group: "solicitacao", defaultSelected: true },
  { key: "channel", label: "Canal", group: "solicitacao", defaultSelected: true },
  { key: "assignee", label: "Responsável", group: "solicitacao", defaultSelected: false },
  { key: "agentName", label: "Agente de IA", group: "solicitacao", defaultSelected: false },
  { key: "internalNotes", label: "Anotações internas", group: "historico", defaultSelected: false, async: true },
  { key: "systemLogs", label: "Registros do sistema", group: "historico", defaultSelected: false, async: true },
  { key: "messageHistory", label: "Histórico de mensagens", group: "historico", defaultSelected: false, async: true },
];

export const ASYNC_HISTORY_FIELD_KEYS = ["internalNotes", "systemLogs", "messageHistory"];

export function getDefaultSelectedExportKeys() {
  return CONVERSATION_EXPORT_FIELDS.filter((f) => f.defaultSelected).map((f) => f.key);
}

function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+\n/g, "\n")
    .trim();
}

function formatDisplayDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    const raw = String(value).trim();
    const mysqlRegex = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})$/;
    const match = mysqlRegex.exec(raw);
    if (match) {
      const [, year, month, day, time] = match;
      return `${day}/${month}/${year} ${time}`;
    }
    return raw;
  }
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function getSafeDate(row, candidates) {
  const raw = candidates.find((v) => v != null && v !== "") ?? null;
  if (!raw) return "";
  return formatDisplayDate(raw);
}

function sanitizeText(text) {
  if (!text) return "";
  return stripHtml(text) || "";
}

function resolveSubtypes(row, typeIdToName = {}) {
  const fromNames = [row?.subtype1Name, row?.subtype2Name, row?.subtype3Name]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  if (fromNames.length) return fromNames.join(" / ");
  if (row?.subtypesLabel && row.subtypesLabel !== "—") return String(row.subtypesLabel);
  const names = [row?.subtype1, row?.subtype2, row?.subtype3]
    .filter((id) => id != null && id !== "")
    .map((id) => typeIdToName[String(id)])
    .filter(Boolean);
  return names.join(" / ");
}

function getMessageTimestamp(msg) {
  return msg?.createdAt || msg?.timestamp || msg?.lastUpdate || msg?.updatedAt || null;
}

function getMessageSenderLabel(msg) {
  const type = String(msg?.type || "").toLowerCase();
  if (type === "note" || type === "xpro") return msg?.senderName || "Anotação interna";
  if (type === "log") return msg?.senderName || "Sistema";

  const senderType = String(msg?.senderType || "").toLowerCase();
  const sender = String(msg?.sender || "").toLowerCase();

  if (sender === "cliente" || sender === "client" || senderType === "client" || senderType === "customer") {
    return msg?.senderName || "Cliente";
  }
  if (senderType === "ai_agent" || senderType === "chatbot") {
    return msg?.senderName || "Agente de IA";
  }
  return msg?.senderName || msg?.userName || "Equipe";
}

function getMessageBody(msg) {
  const text = sanitizeText(msg?.textPlain || msg?.text || "");
  if (text) return text;
  const type = String(msg?.type || "").toLowerCase();
  if (type === "log") return "—";
  return type ? `[${type}]` : "—";
}

export function formatInternalNotes(messages = []) {
  return formatMessagesBlock((messages || []).filter(isInternalNoteType));
}

function sortMessagesChronologically(messages = []) {
  return [...messages].sort((a, b) => {
    const ta = new Date(getMessageTimestamp(a) || 0).getTime();
    const tb = new Date(getMessageTimestamp(b) || 0).getTime();
    if (ta !== tb) return ta - tb;
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });
}

function formatMessagesBlock(messages = [], { includeVia = false } = {}) {
  const sorted = sortMessagesChronologically(messages);
  if (!sorted.length) return "";

  return sorted
    .map((msg) => {
      const when = formatDisplayDate(getMessageTimestamp(msg));
      const author = getMessageSenderLabel(msg);
      const body = getMessageBody(msg);
      if (!includeVia) return `[${when}] ${author}: ${body}`;
      const via = msg?.channelName || msg?.channel || "";
      const viaPart = via ? ` (${via})` : "";
      return `[${when}] ${author}${viaPart}: ${body}`;
    })
    .join("\n\n");
}

export function formatSystemLogs(messages = []) {
  return formatMessagesBlock((messages || []).filter(isLogType));
}

export function formatMessageHistory(messages = []) {
  return formatMessagesBlock((messages || []).filter(isConversationMessage), { includeVia: true });
}

export function needsMessagesForFields(selectedKeys = []) {
  const keySet = new Set(selectedKeys);
  return CONVERSATION_EXPORT_FIELDS.some((f) => f.async && keySet.has(f.key));
}

export function getFieldValue(key, row, context = {}) {
  const { typeIdToName = {}, messagesByLeadId = {} } = context;
  const messages = messagesByLeadId[String(row?.id)] || messagesByLeadId[row?.id] || [];

  switch (key) {
    case "id":
      return row?.id ?? "";
    case "name":
      return row?.name ?? "";
    case "lastMessage":
      return sanitizeText(row?.lastMessage);
    case "createdAt":
      return getSafeDate(row, [
        row?.createdAt,
        row?.created_at,
        row?.data_abertura,
        row?.data_criacao,
        row?.date,
      ]);
    case "lastUpdate":
      return getSafeDate(row, [
        row?.lastUpdate,
        row?.updatedAt,
        row?.updated_at,
        row?.data_ultima_atualizacao,
        row?.timestamp,
      ]);
    case "typeName":
      return row?.typeName || (row?.typeId != null ? typeIdToName[String(row.typeId)] : "") || "";
    case "subtypes":
      return resolveSubtypes(row, typeIdToName);
    case "status":
      return row?.statusLabel ?? row?.status ?? "";
    case "channel":
      return row?.channel ?? "";
    case "assignee":
      return row?.assignedTo ?? row?.assignee?.label ?? row?.userName ?? "";
    case "agentName":
      return row?.agentName ?? "";
    case "internalNotes":
      return formatInternalNotes(messages);
    case "systemLogs":
      return formatSystemLogs(messages);
    case "messageHistory":
      return formatMessageHistory(messages);
    default:
      return "";
  }
}

export function getFieldLabel(key) {
  return CONVERSATION_EXPORT_FIELDS.find((f) => f.key === key)?.label || key;
}
